/**
 * usePlanQualityGate — Group C from usePlanOrchestration.
 *
 * Quality gate and phase-modification handlers:
 *   handleQualityGate, modifyPhase, refineRoadmapWithChat, modifyRoadmapPhase
 */
import { useCallback } from 'react';
import type React from 'react';
import { usePlanStore } from '@/stores/ai/plan-store';
import { useDebugStore } from '@/stores/ai/debug-store';
import { planApi } from '@/services/api/agentApi';
import type { PlanPipelineStage } from '@/types/plan';
import type { PlanOrchestrationDeps } from './usePlanOrchestration';
import { captureDebugPrompts } from './planHelpers';

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces
// ─────────────────────────────────────────────────────────────────────────────

export interface QualityGateResult {
  handleQualityGate: (action: 'proceed' | 'revise' | 'skip' | 'discard') => void;
  modifyPhase: (phaseId: string, opts?: { criterionName?: string }) => Promise<void>;
  refineRoadmapWithChat: () => void;
  modifyRoadmapPhase: (phaseId: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function usePlanQualityGate(
  deps: PlanOrchestrationDeps,
  advanceStage: (stage: PlanPipelineStage) => void,
  addUIMessage: (text: string, role: 'assistant' | 'user', meta?: Record<string, unknown>) => void,
  abortRef: React.RefObject<AbortController | null>,
): QualityGateResult {
  const planStore = usePlanStore();
  const debug = useDebugStore;

  // ── handleQualityGate ──────────────────────────────────────────────────────

  const handleQualityGate = useCallback((action: 'proceed' | 'revise' | 'skip' | 'discard') => {
    planStore.resolveQualityGate(action);

    switch (action) {
      case 'proceed':
        // Pipeline continues from current stage
        break;
      case 'revise':
        advanceStage('conversing');
        planStore.setPlanReady(false);
        break;
      case 'skip':
        // Skip current stage, advance
        break;
      case 'discard':
        planStore.reset();
        break;
    }
  }, [planStore]);

  // ── modifyPhase ────────────────────────────────────────────────────────────

  const modifyPhase = useCallback(async (phaseId: string, opts?: { criterionName?: string }) => {
    const phase = planStore.phases.find(p => p.id === phaseId || p.phaseId === phaseId);
    if (!phase) return;

    const audit = planStore.phaseAudits[phase.phaseId] ?? planStore.phaseAudits[phaseId];

    console.log('[Plan] modifyPhase called:', {
      phaseId,
      'phase.id': phase.id,
      'phase.phaseId': phase.phaseId,
      auditKeys: Object.keys(planStore.phaseAudits),
      auditFound: !!audit,
      auditVerdict: audit?.verdict,
      auditScore: audit?.overallScore,
    });

    // If audit has issues, auto-refine via LLM — no user intervention needed
    if (audit && audit.verdict !== 'pass') {
      const isDebug = debug.getState().isEnabled;
      const weakCriteria = audit.criteria.filter(c => c.verdict !== 'pass');
      const weakSummary = weakCriteria
        .map(c => `• ${c.name} (${c.score}/100) — ${c.reasoning}`)
        .join('\n');

      // Show progress in chat
      addUIMessage(
        `Auto-refining Phase ${phase.phaseNumber}: "${phase.title}" (${audit.overallScore}/100 ${audit.verdict.toUpperCase()})` +
        (opts?.criterionName ? `\nTargeting: ${opts.criterionName}` : '') +
        `\n\nAddressing:\n${weakSummary}`,
        'assistant',
        { source: 'plan-mode', modifyingPhaseId: phaseId },
      );

      // Build targeted audit feedback — if a specific criterion was selected, emphasize it
      const auditFeedback = opts?.criterionName
        ? {
            ...audit,
            // Put the targeted criterion front and center for the LLM
            _targetCriterion: opts.criterionName,
            suggestions: [
              ...(audit.criteria.find(c => c.name === opts.criterionName)?.suggestions ?? []),
              ...audit.suggestions,
            ],
          }
        : audit;

      try {
        planStore.setAuditingPhases(true);

        // Regenerate the phase prompt using audit feedback (same as the auto-retry loop)
        const regenResult = await planApi.regeneratePhase(
          phase.phaseId, auditFeedback, planStore.roadmap,
          abortRef.current?.signal, isDebug,
        );
        planStore.incrementLLMCalls();
        captureDebugPrompts(regenResult, 'phase_regenerate', phase.phaseId);

        // Update the phase in the store
        if (regenResult.data?.phase) {
          const phases = [...planStore.phases];
          const idx = phases.findIndex(p => p.id === phase.id);
          if (idx !== -1) {
            phases[idx] = { ...phases[idx], executionPrompt: regenResult.data.phase.executionPrompt };
            planStore.setPhases(phases);
          }
        }

        // Re-audit the updated phase
        const updatedPhase = planStore.phases.find(p => p.id === phase.id);
        const currentRoadmap = planStore.roadmap;
        if (updatedPhase && currentRoadmap) {
          const reAudited = await planApi.auditPhases(
            [updatedPhase], currentRoadmap,
            abortRef.current?.signal, isDebug,
          );
          planStore.incrementLLMCalls();
          captureDebugPrompts(reAudited, 'phase_audit', phase.phaseId);

          if (reAudited.data?.phaseAudits?.[phase.phaseId]) {
            planStore.setPhaseAudit(phase.phaseId, reAudited.data.phaseAudits[phase.phaseId]);
          }
        }

        const newAudit = planStore.phaseAudits[phase.phaseId];
        addUIMessage(
          `Phase ${phase.phaseNumber} refined: ${newAudit?.overallScore ?? '?'}/100 (${newAudit?.verdict?.toUpperCase() ?? 'unknown'}).` +
          `\nExpand the phase to review the updated prompt and audit.`,
          'assistant',
          { source: 'plan-mode' },
        );
      } catch (err) {
        console.error('[Plan] Auto-refine failed:', err);
        addUIMessage(
          `Auto-refine failed for Phase ${phase.phaseNumber}. You can try again or edit the prompt manually.`,
          'assistant',
          { source: 'plan-mode' },
        );
      } finally {
        planStore.setAuditingPhases(false);
      }
      return;
    }

    // No audit issues (pass or no audit) — drop into manual chat
    advanceStage('conversing');
    addUIMessage(
      `Modifying Phase ${phase.phaseNumber}: ${phase.title}. Describe your changes and I'll update the execution prompt.`,
      'assistant',
      { source: 'plan-mode', modifyingPhaseId: phaseId },
    );
  }, [planStore]);

  // ── refineRoadmapWithChat ────────────────────────────────────────────────

  const refineRoadmapWithChat = useCallback(() => {
    const roadmap = planStore.roadmap;
    if (!roadmap) return;
    // Keep the roadmap in state (do NOT clear it)
    advanceStage('conversing');
    planStore.setPlanReady(false);
    const contextMsg = `I have your current roadmap "${roadmap.title}" with ${roadmap.phases.length} phase${roadmap.phases.length !== 1 ? 's' : ''}. What changes would you like me to make? You can ask me to:\n- Add, remove, or reorder phases\n- Change the scope of specific phases\n- Adjust complexity estimates\n- Restructure dependencies`;
    addUIMessage(contextMsg, 'assistant', { source: 'plan-mode', refiningRoadmap: true, roadmapId: roadmap.id });
    planStore.addAssistantMessage(contextMsg);
  }, [planStore]);

  // ── modifyRoadmapPhase ───────────────────────────────────────────────────

  const modifyRoadmapPhase = useCallback((phaseId: string) => {
    const roadmap = planStore.roadmap;
    if (!roadmap) return;
    const phase = roadmap.phases.find(p => p.id === phaseId);
    if (!phase) return;
    // Keep the roadmap in state
    advanceStage('conversing');
    planStore.setPlanReady(false);
    const contextMsg = `Modifying Phase ${phase.phaseNumber}: "${phase.title}" (${phase.domain}, ${phase.estimatedComplexity} complexity).\n\nCurrent tasks:\n${phase.tasks.map((t, i) => `${i + 1}. ${t}`).join('\n')}\n\nDescribe what you'd like to change about this phase.`;
    addUIMessage(contextMsg, 'assistant', { source: 'plan-mode', modifyingRoadmapPhaseId: phaseId, roadmapId: roadmap.id });
    planStore.addAssistantMessage(contextMsg);
  }, [planStore]);

  return {
    handleQualityGate,
    modifyPhase,
    refineRoadmapWithChat,
    modifyRoadmapPhase,
  };
}
